/**
 * TokenAdapter - Centralized token-shape conversion for BattleRoom
 * 
 * date_of_change: 2025-10-18
 * rule_reference: AGENTS.md — Separate rules engine from presentation; single source of truth for schema and projections.
 * purpose: Replace scattered, duplicated conversions of character/monster data into token fields
 *          with a deterministic, reusable adapter. This adapter only builds the token "core"
 *          (stats and data attachments). Placement (id, x, y, layer, draggable, src) remains
 *          the responsibility of callers (e.g., useBattleRoom).
 */

import { creatureMechanicsProjection } from '../utils/combat/CreatureAbilityCatalog';
import { buildCombatActionEconomy } from '../utils/combat/CombatActionCatalog';
import {
  buildShortAbilityBlock,
  parseChallengeRating,
  resolveCombatantChallengeDisplay,
  resolveCombatantProficiencyBonus,
  resolveCombatantSpeedFeet,
  resolvePreferredArmorClass,
  resolvePreferredInitiative,
  resolvePreferredAbilityScores
} from '../utils/combat/CombatantNormalizer';

const TokenAdapter = {
  /**
   * Convert an entity into a core token shape used by BattleRoom.
   * Does NOT set: id, x, y, layer, draggable, src.
   * 
   * @param {object} entityData - Source entity (UDM display or canonical-like object)
   * @returns {object} Partial token with hp/hpObj/maxHp/ac/speed/abilities/short abilities, CR and canonical attachments
   */
  toTokenCore(entityData) {
    if (!entityData || typeof entityData !== 'object') return {};

    // Prefer provided display shape, fall back to raw entity
    const schemaData = entityData.schemaData || entityData.data || entityData.originalUnifiedEntity?.data || entityData.canonicalData || null;
    const displayData = schemaData || entityData;
    const canonicalData = entityData.canonicalData || entityData.originalUnifiedEntity?.data || entityData.data || entityData.schemaData || {};

    // Normalize type detection
    const normalizedType = (displayData.type || entityData.type || (displayData.creatureType ? 'monster' : 'character') || 'character').toString().toLowerCase();
    const toNumber = (value) => {
      if (typeof value === 'number') return Number.isFinite(value) ? value : null;
      if (typeof value === 'string') {
        const match = value.match(/-?\d+(?:\.\d+)?/);
        if (!match) return null;
        const parsed = Number(match[0]);
        return Number.isFinite(parsed) ? parsed : null;
      }
      if (value && typeof value === 'object') {
        return toNumber(value.value ?? value.base ?? value.modifier ?? value.initiative ?? value.proficiencyBonus);
      }
      return null;
    };

    const normalizeStructuredHP = (hpData) => {
      if (!hpData || typeof hpData !== 'object') return null;

      if ('current' in hpData || 'max' in hpData || 'maximum' in hpData || 'value' in hpData) {
        const current = parseInt(hpData.current ?? hpData.value ?? hpData.average, 10) || 0;
        const max = parseInt(hpData.max ?? hpData.maximum ?? hpData.current ?? hpData.value ?? hpData.average, 10) || current || 0;
        return { current, max };
      }

      if ('average' in hpData) {
        const average = parseInt(hpData.average, 10) || 0;
        return { current: average, max: average };
      }

      return null;
    };

    // HP normalization
    const unifyHP = (src) => {
      const runtimeCandidates = [entityData, src].filter((candidate) => candidate && typeof candidate === 'object');

      for (const candidate of runtimeCandidates) {
        if (candidate.hpObj && typeof candidate.hpObj === 'object') {
          const cur = parseInt(candidate.hpObj.current, 10) || 0;
          const mx = parseInt(candidate.hpObj.max ?? candidate.hpObj.current, 10) || 0;
          return { current: cur, max: mx };
        }
        if (typeof candidate.hp === 'number' || typeof candidate.hp === 'string') {
          const cur = parseInt(candidate.hp, 10) || 0;
          const mx = parseInt(candidate.maxHp, 10) || cur || 0;
          return { current: cur, max: mx };
        }
      }

      if (!src || typeof src !== 'object') return { current: 0, max: 0 };

      const structuredSources = [
        src.hp,
        src.hitPoints,
        src.defense?.hp,
        entityData.hp,
        entityData.hitPoints,
        entityData.defense?.hp,
        entityData.data?.hp,
        entityData.data?.hitPoints,
        entityData.data?.defense?.hp,
        canonicalData.hp,
        canonicalData.hitPoints,
        canonicalData.defense?.hp,
        entityData.hitPoints
      ];

      for (const candidate of structuredSources) {
        const normalized = normalizeStructuredHP(candidate);
        if (normalized) {
          return normalized;
        }
      }

      if (typeof entityData.data?.hp === 'number') {
        return { current: entityData.data.hp, max: entityData.data.hp };
      }

      return { current: 0, max: 0 };
    };

    // AC normalization
    const normalizeAC = () => resolvePreferredArmorClass({
      runtimeSources: [
        displayData,
        entityData
      ],
      canonicalSources: [
        entityData.schemaData,
        entityData.data,
        canonicalData,
        entityData.originalUnifiedEntity?.data
      ],
      runtimeOverrideSource: entityData
    });

    const resolveCharacterLevel = () => {
      const directCandidates = [
        displayData.level,
        entityData.level,
        entityData.data?.level,
        canonicalData.level
      ];

      for (const candidate of directCandidates) {
        const numeric = toNumber(candidate);
        if (numeric !== null) return numeric;
      }

      const classLists = [
        displayData.classes,
        entityData.classes,
        entityData.data?.classes,
        canonicalData.classes
      ];

      for (const classList of classLists) {
        if (!Array.isArray(classList) || classList.length === 0) continue;
        const totalLevel = classList.reduce((sum, cls) => {
          const numeric = toNumber(cls?.level);
          return numeric !== null ? sum + numeric : sum;
        }, 0);
        if (totalLevel > 0) return totalLevel;
      }

      return null;
    };

    const abilities = resolvePreferredAbilityScores({
      runtimeSources: [
        entityData.abilities,
        entityData.stats,
        entityData
      ],
      canonicalSources: [
        schemaData,
        canonicalData,
        entityData.originalUnifiedEntity?.data
      ],
      runtimeOverrideSource: entityData,
      fallbackValue: 10
    });

    const abilitiesShort = buildShortAbilityBlock(abilities, 10);

    const resolveSpeed = () => {
      return resolveCombatantSpeedFeet(displayData, entityData, entityData.schemaData, entityData.data, canonicalData);
    };

    const resolveInitiative = () => {
      const preferredInitiative = resolvePreferredInitiative({
        runtimeSources: [
          displayData,
          entityData
        ],
        canonicalSources: [
          entityData.schemaData,
          entityData.data,
          canonicalData,
          entityData.originalUnifiedEntity?.data
        ],
        runtimeOverrideSource: entityData
      });

      if (preferredInitiative !== null) {
        return preferredInitiative;
      }

      if (typeof abilities.dexterity === 'number') {
        return Math.floor((abilities.dexterity - 10) / 2);
      }

      return null;
    };

    const resolveProficiencyBonus = () => {
      const direct = resolveCombatantProficiencyBonus(
        displayData,
        entityData,
        entityData.schemaData,
        entityData.data,
        canonicalData
      );
      if (direct !== null) {
        return direct;
      }

      const level = resolveCharacterLevel();
      return level !== null ? Math.ceil(level / 4) + 1 : null;
    };

    const resolveChallengeDisplay = () => {
      return resolveCombatantChallengeDisplay(
        displayData,
        entityData,
        entityData.schemaData,
        entityData.data,
        canonicalData
      );
    };

    const actionEconomy = buildCombatActionEconomy(entityData);
    const resolvedChallengeDisplay = resolveChallengeDisplay();
    const resolvedChallengeRating = parseChallengeRating(resolvedChallengeDisplay);

    // The authoritative damage resolver intentionally fails closed when a
    // combatant has no canonical mitigation block. TokenAdapter is the
    // first-class entity -> BattleRoom boundary, so even creatures with no
    // resistances must cross it with explicit empty arrays. This keeps
    // built-in actors (such as Goblin) combat-ready without weakening the
    // server's validation for incomplete custom snapshots.
    // date_of_change: 2026-08-26
    const firstDamageList = (...candidates) => {
      for (const candidate of candidates) {
        if (Array.isArray(candidate)) return candidate;
      }
      return [];
    };
    const suppliedResistanceBlock = (
      entityData.resistanceBlock
      ?? displayData.resistanceBlock
      ?? canonicalData.resistanceBlock
      ?? entityData.data?.resistanceBlock
      ?? entityData.schemaData?.resistanceBlock
    );
    const resistanceBlock = {
      resist: firstDamageList(
        suppliedResistanceBlock?.resist,
        entityData.resistances,
        entityData.damageResistances,
        displayData.resistances,
        displayData.damageResistances,
        canonicalData.resistances,
        canonicalData.damageResistances,
        canonicalData.damageModifiers?.resistances
      ),
      immune: firstDamageList(
        suppliedResistanceBlock?.immune,
        entityData.immunities,
        entityData.damageImmunities,
        displayData.immunities,
        displayData.damageImmunities,
        canonicalData.immunities,
        canonicalData.damageImmunities,
        canonicalData.damageModifiers?.immunities
      ),
      vuln: firstDamageList(
        suppliedResistanceBlock?.vuln,
        entityData.vulnerabilities,
        entityData.damageVulnerabilities,
        displayData.vulnerabilities,
        displayData.damageVulnerabilities,
        canonicalData.vulnerabilities,
        canonicalData.damageVulnerabilities,
        canonicalData.damageModifiers?.vulnerabilities
      )
    };

    // Compose core fields
    const hpObj = unifyHP(displayData);
    const core = {
      name: displayData.name || entityData.name || 'Unknown Entity',
      type: normalizedType,
      // HP
      hpObj,
      hp: hpObj.current,
      maxHp: hpObj.max,
      // AC/speed
      ac: normalizeAC(),
      speed: resolveSpeed(),
      initiative: resolveInitiative(),
      proficiencyBonus: resolveProficiencyBonus(),
      // CR info (monster)
      cr: resolvedChallengeDisplay,
      challengeRating: resolvedChallengeRating ?? resolvedChallengeDisplay,
      resistanceBlock,
      // Abilities
      abilities,
      abilitiesShort,
      schemaData: schemaData || undefined,
      canonicalData: canonicalData || undefined,
      originalUnifiedEntity: entityData.originalUnifiedEntity || undefined
    };

    // Authored defenses, saves and traits travel with each token instance.
    // SRD 5.1 monster statistics; date_of_change: 2026-09-05.
    Object.assign(core, creatureMechanicsProjection(entityData));

    // Attach canonical data blocks for sheets
    if (normalizedType === 'monster') {
      const canonicalMonsterData = entityData.canonicalData || entityData.data || entityData;
      core.monsterData = {
        ...entityData,
        ...canonicalMonsterData,
        abilities: canonicalMonsterData.abilities || abilities,
        stats: canonicalMonsterData.stats || {
          str: abilities.strength,
          dex: abilities.dexterity,
          con: abilities.constitution,
          int: abilities.intelligence,
          wis: abilities.wisdom,
          cha: abilities.charisma
        },
        speed: core.speed,
        challengeRating: core.challengeRating,
        proficiencyBonus: core.proficiencyBonus,
        data: canonicalMonsterData
      };
    } else {
      const characterSchemaData = schemaData || canonicalData || entityData;
      const resolvedLevel = resolveCharacterLevel();
      const resolvedCharacterClass = (
        displayData.class
        || entityData.class
        || characterSchemaData?.class
        || (Array.isArray(characterSchemaData?.classes) ? characterSchemaData.classes[0]?.name : null)
      );
      const resolvedRace = (
        displayData.race
        || entityData.race
        || characterSchemaData?.identity?.race
        || characterSchemaData?.race
      );
      const resolvedBackground = (
        displayData.background
        || entityData.background
        || characterSchemaData?.identity?.background
        || characterSchemaData?.background
      );
      const resolvedAlignment = (
        displayData.alignment
        || entityData.alignment
        || characterSchemaData?.identity?.alignment
        || characterSchemaData?.alignment
      );
      const resolvedSpells = (
        entityData.spells
        ?? entityData.characterData?.spells
        ?? entityData.schemaData?.spells
        ?? entityData.data?.spells
        ?? entityData.data?.characterData?.spells
        ?? canonicalData.spells
        ?? characterSchemaData?.spells
        ?? characterSchemaData?.compat?.originalData?.spells
      );
      const resolvedSpellcasting = (
        entityData.spellcasting
        ?? entityData.characterData?.spellcasting
        ?? entityData.schemaData?.spellcasting
        ?? entityData.data?.spellcasting
        ?? entityData.data?.characterData?.spellcasting
        ?? canonicalData.spellcasting
        ?? characterSchemaData?.spellcasting
        ?? characterSchemaData?.compat?.originalData?.spellcasting
      );
      const resolvedSkills = (
        entityData.skills
        ?? entityData.schemaData?.skills
        ?? entityData.data?.skills
        ?? canonicalData.skills
        ?? characterSchemaData?.compat?.originalData?.skills
      );
      const resolvedSavingThrows = (
        entityData.savingThrows
        ?? entityData.schemaData?.savingThrows
        ?? entityData.data?.savingThrows
        ?? canonicalData.savingThrows
        ?? characterSchemaData?.compat?.originalData?.savingThrows
      );
      const resolvedEquipment = (
        entityData.equipment
        ?? entityData.schemaData?.equipment
        ?? entityData.data?.equipment
        ?? canonicalData.equipment
        ?? characterSchemaData?.compat?.originalData?.equipment
      );

      // Character core (non-transforming attachment)
      core.characterData = {
        id: entityData.id,
        name: displayData.name || entityData.name,
        class: resolvedCharacterClass,
        level: resolvedLevel,
        race: resolvedRace,
        background: resolvedBackground,
        alignment: resolvedAlignment,
        hp: core.hp,
        maxHp: core.maxHp,
        ac: core.ac,
        speed: core.speed,
        initiative: core.initiative,
        proficiencyBonus: core.proficiencyBonus,
        abilities: abilities,
        stats: entityData.stats,
        combatStats: {
          armorClass: core.ac,
          initiative: core.initiative,
          speed: core.speed,
          proficiencyBonus: core.proficiencyBonus
        },
        actions: {
          actions: actionEconomy.actions,
          bonusActions: actionEconomy.bonusActions,
          reactions: actionEconomy.reactions
        },
        bonusActions: actionEconomy.bonusActions,
        reactions: actionEconomy.reactions,
        spells: resolvedSpells,
        spellcasting: resolvedSpellcasting,
        preparedSpells: entityData.preparedSpells
          ?? entityData.characterData?.preparedSpells
          ?? entityData.data?.preparedSpells
          ?? characterSchemaData?.preparedSpells,
        skills: resolvedSkills,
        savingThrows: resolvedSavingThrows,
        equipment: resolvedEquipment,
        appearance: entityData.appearance,
        backstory: entityData.backstory,
        data: entityData.data || entityData.schemaData || canonicalData,
        schemaData: entityData.schemaData || entityData.data || canonicalData,
        canonicalData: entityData.canonicalData || entityData.schemaData || entityData.data || canonicalData,
        originalUnifiedEntity: entityData.originalUnifiedEntity || null,
        compat: entityData.compat || entityData.schemaData?.compat || canonicalData.compat
      };
    }

    return core;
  }
};

export default TokenAdapter;
